import {PackingSlipTemplateRecord} from 'src/interfaces/PackingSlipTemplate.interface'

export type PackingSlipCustomSettingsProps = {
  template: PackingSlipTemplateRecord
  onChange: (patch: Partial<PackingSlipTemplateRecord>) => void
}

const CUSTOM_PAPER_SIZE_OPTIONS = [
  {value: 'letter', label: 'Letter'},
  {value: 'a4', label: 'A4'},
  {value: '4x6', label: '4" x 6" Thermal'},
] as const

const normalizeOrientation = (value: string | undefined): 'portrait' | 'landscape' =>
  String(value ?? 'portrait').toLowerCase() === 'landscape' ? 'landscape' : 'portrait'

const PackingSlipCustomSettings = ({
  template,
  onChange,
}: PackingSlipCustomSettingsProps) => {
  const orientation = normalizeOrientation(template.orientation)
  const marginDisplay =
    template.marginMm === undefined || template.marginMm === null
      ? ''
      : String(template.marginMm)

  return (
    <div className='packing-slip-custom-settings'>
      <div className='packing-slip-custom-settings__row packing-slip-custom-settings__row--two-col'>
        <div className='account-settings-modern__form-group'>
          <label>Paper Size</label>
          <select
            value={template.paperSize || '4x6'}
            onChange={(e) => onChange({paperSize: e.target.value})}
          >
            {CUSTOM_PAPER_SIZE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className='account-settings-modern__form-group'>
          <label>Margin in mm</label>
          <input
            type='number'
            min={0}
            step={1}
            value={marginDisplay}
            onChange={(e) => {
              const raw = e.target.value
              if (raw === '') {
                onChange({marginMm: 0})
                return
              }
              const parsed = Number(raw)
              onChange({
                marginMm: Number.isFinite(parsed) ? Math.max(0, parsed) : 0,
              })
            }}
            placeholder='0'
          />
        </div>
      </div>

      <div className='account-settings-modern__form-group'>
        <label>Orientation</label>
        <div className='packing-slip-segmented' role='group' aria-label='Page orientation'>
          <button
            type='button'
            className={`packing-slip-segmented__option ${orientation === 'portrait' ? 'is-active' : ''}`}
            onClick={() => onChange({orientation: 'portrait'})}
          >
            Portrait
          </button>
          <button
            type='button'
            className={`packing-slip-segmented__option ${orientation === 'landscape' ? 'is-active' : ''}`}
            onClick={() => onChange({orientation: 'landscape'})}
          >
            Landscape
          </button>
        </div>
      </div>

      <div className='account-settings-modern__form-group'>
        <label>Downloaded PDF filename</label>
        <input
          type='text'
          value={template.pdfFileName ?? ''}
          onChange={(e) => onChange({pdfFileName: e.target.value})}
          placeholder='packing-slip-{{order.number}}'
        />
        <p className='packing-slip-custom-settings__help-text'>
          Use shortcodes in the filename, for example{' '}
          <code>packing-slip-{'{{order.number}}'}</code>.
        </p>
      </div>

      <div className='packing-slip-custom-settings__checkbox-row account-settings-modern__checkbox-row'>
        <span>Repeat table header on every printed page</span>
        <input
          type='checkbox'
          checked={Boolean(template.repeatTableHeader)}
          onChange={(e) => onChange({repeatTableHeader: e.target.checked})}
        />
      </div>

      <div className='packing-slip-custom-settings__checkbox-row account-settings-modern__checkbox-row'>
        <span>Block saving if template has unknown shortcodes</span>
        <input
          type='checkbox'
          checked={Boolean(template.blockSavingUnknownSourceCode)}
          onChange={(e) =>
            onChange({blockSavingUnknownSourceCode: e.target.checked})
          }
        />
      </div>
    </div>
  )
}

export {PackingSlipCustomSettings}
export default PackingSlipCustomSettings
